import { useEffect, useState } from 'react';
import { listByCategory, tickerLabel, type Category, getTicker } from '@/content/tickers';
import { priceAt, tickerHasActiveImpulse } from '@/engine/prices';
import { useMarkets } from '@/state/markets';
import { priceFmt, pct } from '@/lib/format';
import { cn } from '@/lib/cn';

type TabId = Category | 'mine';

const CATEGORIES: { id: TabId; label: string }[] = [
  { id: 'mine', label: 'MINE' },
  { id: 'company', label: 'CO' },
  { id: 'crypto', label: 'CRY' },
  { id: 'mood', label: 'MOOD' },
  { id: 'prediction', label: '?' },
];

// Sector roll-ups — same baskets that correlation engine uses, so the strip
// gives visible feedback when an S-tier headline lights up its basket.
const SECTORS: { id: string; label: string; tickers: string[] }[] = [
  { id: 'war', label: 'WAR', tickers: ['BOOM', 'WALL', 'TARI', 'GRFT', 'NUKE', 'GUNZ'] },
  { id: 'tech', label: 'TECH', tickers: ['CHIP', 'OAEN', 'AGI', 'FAKE', 'SAAS'] },
  { id: 'crypto', label: 'CRYPTO', tickers: ['BTCO', 'DOGE2', 'JEET', 'RUGS', 'PEPE7'] },
  { id: 'consumer', label: 'CONS', tickers: ['BURG', 'MEAL', 'ZZZZ', 'VAPE'] },
];

export function Watchlist() {
  const [now, setNow] = useState(() => Date.now());
  const [tab, setTab] = useState<TabId>('company');
  const [filter, setFilter] = useState('');
  const activeId = useMarkets((s) => s.activeTickerId);
  const setActive = useMarkets((s) => s.setActiveTicker);
  const positions = useMarkets((s) => s.positions);
  const heldSet = new Set(positions.map((p) => p.ticker));

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const baseTickers = tab === 'mine'
    ? positions.map((p) => getTicker(p.ticker)).filter((t): t is NonNullable<typeof t> => t != null)
    : listByCategory(tab);
  const filterLc = filter.trim().toLowerCase();
  const tickers = filterLc
    ? baseTickers.filter((t) => t.id.toLowerCase().includes(filterLc) || tickerLabel(t).toLowerCase().includes(filterLc))
    : baseTickers;

  return (
    <aside className="bg-[var(--bg-surface)] sm:overflow-y-auto">
      {/* Sector pulse: average 24h % across each basket. Visual confirmation
          for correlation engine — when president bombs Belgium, WAR lights up.
          Mobile: single horizontal row. Desktop: 2x2 grid. */}
      <div className="flex border-b border-[var(--border-subtle)] bg-[var(--bg-deep)] sm:grid sm:grid-cols-2">
        {SECTORS.map((sec) => {
          let total = 0, count = 0;
          for (const tid of sec.tickers) {
            const t = getTicker(tid);
            if (!t) continue;
            const p = priceAt(t, now);
            const ref = priceAt(t, now - 24 * 60 * 60 * 1000);
            if (ref > 0) { total += p / ref - 1; count++; }
          }
          const avg = count ? total / count : 0;
          const positive = avg >= 0;
          return (
            <div
              key={sec.id}
              className="flex flex-1 items-center justify-between gap-1 border-r border-[var(--border-subtle)] px-2 py-1 last:border-r-0 sm:border-b"
            >
              <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)]">{sec.label}</span>
              <span className={cn(
                'text-[10px] tabular-nums',
                positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]',
              )}>
                {positive ? '▲' : '▼'} {(avg * 100).toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
      {/* Filter input — visible on desktop only; mobile horizontal scroll keeps things compact */}
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="filter…"
        className="hidden sm:block w-full border-b border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1 text-mono-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none focus:border-[var(--accent-primary)]"
      />
      {/* Category tabs */}
      <div className="flex border-b border-[var(--border-subtle)] bg-[var(--bg-deep)]">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setTab(c.id)}
            className={cn(
              'flex-1 border-b-2 py-1.5 text-mono-xs font-bold uppercase tracking-wider transition-colors',
              tab === c.id
                ? 'border-[var(--accent-primary)] text-[var(--accent-primary)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>
      {/* Mobile: horizontal strip; sm+: vertical list */}
      <div className="flex overflow-x-auto sm:flex-col sm:overflow-x-visible">
        {tickers.length === 0 && (
          <div className="px-3 py-3 text-mono-xs italic text-[var(--text-muted)]">
            {tab === 'mine' ? "you own nothing. that's the move actually." : 'empty'}
          </div>
        )}
        {tickers.map((t) => {
          const p = priceAt(t, now);
          // 24h change, not against 2026-01-01 baseline (that produces +79000% reads)
          const ref = priceAt(t, now - 24 * 60 * 60 * 1000);
          const change = ref > 0 ? p / ref - 1 : 0;
          const positive = change >= 0;
          const isActive = t.id === activeId;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              title={tickerLabel(t)}
              className={cn(
                'flex shrink-0 flex-col items-center gap-0.5 border-b-2 px-3 py-1.5 text-left transition-colors sm:w-full sm:flex-row sm:items-center sm:justify-between sm:border-b-0 sm:border-l-2',
                isActive
                  ? 'border-[var(--accent-primary)] bg-[var(--bg-elevated)]'
                  : 'border-transparent hover:bg-[var(--bg-elevated)]',
              )}
            >
              <span className="text-mono-sm font-bold text-[var(--text-primary)]">
                {t.id}
                {heldSet.has(t.id) && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)]" title="You hold a position" />}
                {tickerHasActiveImpulse(t.id) && <span className="ml-1" title="Active news impulse">🔥</span>}
              </span>
              <div className="flex flex-row items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0.5">
                <span className="text-mono-sm tabular-nums text-[var(--text-primary)]">
                  {priceFmt(p)}
                </span>
                <span
                  className={cn(
                    'text-mono-xs tabular-nums',
                    positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]',
                  )}
                >
                  {pct(change)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
